import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import ActionItemCard from './ActionItemCard';
import ActionItemForm from './ActionItemForm';
import RichTextEditor from '../../../common/RichTextEditor';
import { decodeHtml } from '../../../MyActivities/MyCV/helpers/ProcessTemplate';

// Size configurations for variants
const SIZE_CONFIG = {
  full: {
    padding: 2,
    avatarSize: 20,
    dateFormat: 'DD MMM YYYY, HH:mm',
    backgroundColor: 'white',
    expandedBackground: '#f5f7fa',
    borderColor: '#e0e0e0',
    marginBottom: 2,
  },
  compact: {
    padding: 1.5,
    avatarSize: 16,
    dateFormat: 'DD MMM YYYY',
    backgroundColor: '#FAFBFC',
    expandedBackground: '#F3F4F6',
    borderColor: '#E5E7EB',
    marginBottom: 1,
  },
};

/**
 * NoteCard - A reusable card component for displaying meeting notes
 *
 * @param {Object} props
 * @param {Object} props.note - The note object (id, notes/content, addedByEmployee, createdAt)
 * @param {'full' | 'compact'} props.variant - Size variant (default: 'full')
 * @param {boolean} props.isExpanded - Whether the note is expanded
 * @param {boolean} props.isEditing - Whether the note is in edit mode
 * @param {string} props.editingContent - The current editing content
 * @param {Function} props.onToggleExpand - Callback when expand/collapse clicked
 * @param {Function} props.onStartEdit - Callback when Edit button clicked
 * @param {Function} props.onCancelEdit - Callback when Cancel button clicked
 * @param {Function} props.onSaveEdit - Callback when Save button clicked
 * @param {Function} props.onMenuOpen - Callback when menu button clicked
 * @param {Function} props.onEditChange - Callback when editing content changes
 * @param {boolean} props.canEdit - Whether editing is allowed
 * @param {Array} props.actionItems - Action items linked to this note
 * @param {Object} props.actionItemsProps - Props for nested ActionItemCard components
 * @param {boolean} props.showAddActionItemForm - Whether to show add action item form
 * @param {Object} props.newActionItem - New action item data
 * @param {Function} props.onNewActionItemChange - Callback for new action item changes
 * @param {Function} props.onAddActionItem - Callback to add action item
 * @param {Function} props.onCancelAddActionItem - Callback to cancel adding action item
 * @param {Function} props.onAddActionItemClick - Callback when Add Action Item button clicked
 * @param {boolean} props.isAddingActionItem - Loading state for adding action item
 */
const NoteCard = ({
  note,
  variant = 'full',
  isExpanded,
  isEditing,
  editingContent,
  onToggleExpand,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onMenuOpen,
  onEditChange,
  canEdit = true,
  actionItems = [],
  actionItemsProps = {},
  showAddActionItemForm = false,
  newActionItem = {},
  onNewActionItemChange,
  onAddActionItem,
  onCancelAddActionItem,
  onAddActionItemClick,
  isAddingActionItem = false,
}) => {
  const config = SIZE_CONFIG[variant];

  return (
    <Paper
      id={`note-${note.id}`}
      elevation={0}
      sx={{
        border: `1px solid ${config.borderColor}`,
        borderRadius: '4px',
        mb: config.marginBottom,
        overflow: 'hidden',
      }}
    >
      {/* Note Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: config.padding,
          cursor: 'pointer',
          backgroundColor: isExpanded ? config.expandedBackground : config.backgroundColor,
          borderBottom: isExpanded ? `1px solid ${config.borderColor}` : 'none',
        }}
        onClick={onToggleExpand}
      >
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmployeeAvatar employee={note.addedByEmployee} size={config.avatarSize} />
          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
            {note.addedByEmployee?.fullName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            • {dayjs(note.createdAt).format(config.dateFormat)}
          </Typography>
        </Box>
        {canEdit && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onMenuOpen?.(e, note);
              }}
              sx={{ mr: 1 }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            {isExpanded ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </Box>
        )}
        {!canEdit && (
          isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
        )}
      </Box>

      {/* Note Expanded Content */}
      {isExpanded && (
        <Box sx={{ p: config.padding }}>
          {isEditing ? (
            <Box>
              <Box sx={{ mb: 1 }}>
                <RichTextEditor
                  variant="compact"
                  value={editingContent}
                  onChange={onEditChange}
                  minHeight={220}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={onSaveEdit}
                  disabled={!editingContent?.trim()}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onCancelEdit}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  mb: 2,
                  '& p': { margin: 0 },
                  '& ul, & ol': { margin: '8px 0', paddingLeft: '1.5rem' },
                }}
                dangerouslySetInnerHTML={{ __html: decodeHtml(note.notes || note.content || '') }}
              />

              {/* Action Items linked to this note */}
              {actionItems.length > 0 && (
                <Box sx={{ mb: 2, mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                    Action Items ({actionItems.length})
                  </Typography>
                  {actionItems.map((actionItem) => (
                    <ActionItemCard
                      key={actionItem.id}
                      actionItem={actionItem}
                      variant="compact"
                      showFromNote={false}
                      {...actionItemsProps}
                      isExpanded={actionItemsProps.expandedActionItems?.has(actionItem.id)}
                      isEditing={actionItemsProps.editingActionItemId === actionItem.id}
                      onToggleExpand={() => actionItemsProps.onToggleExpand?.(actionItem.id)}
                      onStartEdit={() => actionItemsProps.onStartEdit?.(actionItem)}
                      onSaveEdit={() => actionItemsProps.onSaveEdit?.(actionItem.id)}
                      onDelete={() => actionItemsProps.onDelete?.(actionItem)}
                      onStatusChange={(status) => actionItemsProps.onStatusChange?.(actionItem, status)}
                    />
                  ))}
                </Box>
              )}

              {/* Add Action Item Form for this note */}
              {showAddActionItemForm && (
                <Box sx={{ mb: 2, mt: 2 }}>
                  <ActionItemForm
                    variant="compact"
                    item={newActionItem}
                    onChange={onNewActionItemChange}
                    onSave={onAddActionItem}
                    onCancel={onCancelAddActionItem}
                    isLoading={isAddingActionItem}
                    isNew={true}
                  />
                </Box>
              )}

              {canEdit && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onStartEdit}
                  >
                    Edit
                  </Button>
                  {!showAddActionItemForm && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => onAddActionItemClick?.(note)}
                      sx={{
                        color: '#2563EB',
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Add Action Item
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default NoteCard;
